import React, { useState } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Alert, CircularProgress,
  Skeleton, Chip, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HubStatusBadge from './HubStatusBadge';
import RawCbvMsvModal from './RawCbvMsvModal';
import { getInstanceMonitorMicroservices, registerInstanceInMonitor } from '../../services/apiService';

/**
 * HubMicroservicePanel — table of 26 microservices fetched via Hub API.
 * Port of MicroservicePanel.tsx from highq-instance-monitor.
 *
 * @param {{
 *   instanceName: string,
 *   baseURL?: string,
 *   team?: string,
 *   hubConfigured: boolean
 * }} props
 */
const HubMicroservicePanel = ({ instanceName, baseURL, team, hubConfigured }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawModalOpen, setRawModalOpen] = useState(false);
  const [selectedMsName, setSelectedMsName] = useState(null);

  const fetchMicroservices = async () => {
    if (!instanceName) return;
    setLoading(true);
    setError(null);
    try {
      // Auto-register instance in Python backend if we have the URL
      if (baseURL) {
        try {
          await registerInstanceInMonitor(baseURL, '', team || '');
        } catch {
          // Ignore registration errors (instance may already exist)
        }
      }
      const result = await getInstanceMonitorMicroservices(instanceName);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch microservice data from Hub');
    } finally {
      setLoading(false);
    }
  };

  const handleViewRaw = (msDisplayName) => {
    setSelectedMsName(msDisplayName);
    setRawModalOpen(true);
  };

  if (!hubConfigured) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Hub is not configured. Set <code>HUB_URL</code> and <code>HUB_SHARED_KEY</code> in{' '}
          <code>instance-monitor/.env</code> to enable microservice status.
        </Alert>
      </Box>
    );
  }

  if (!data && !loading && !error) {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Typography color="text.secondary" variant="body2">
          Click below to fetch live microservice status from Hub.
        </Typography>
        <Button variant="contained" size="small" startIcon={<RefreshIcon />} onClick={fetchMicroservices}>
          Fetch from Hub
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 1 }}>
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 0.5, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Alert severity="error">{error}</Alert>
        <Button size="small" startIcon={<RefreshIcon />} onClick={fetchMicroservices} sx={{ alignSelf: 'flex-start' }}>
          Retry
        </Button>
      </Box>
    );
  }

  const microservices = data?.microservices || [];
  const hubMsResolved = data?.hubMsResolved;
  const collab = data?.collab;

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {microservices.length} microservices
          </Typography>
          {!hubMsResolved && (
            <Chip label="Hub MS not resolved" size="small" color="warning" variant="outlined" />
          )}
        </Box>
        <Button size="small" startIcon={<RefreshIcon />} onClick={fetchMicroservices} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {/* Collab instance row */}
      {collab && (
        <Box sx={{ px: 1, pb: 0.5 }}>
          <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <HubStatusBadge status={collab.status} variant="dot" />
            <Typography variant="body2" fontWeight={600}>Collab (main)</Typography>
            <Typography variant="body2" color="text.secondary">{collab.collabVersion || 'N/A'}</Typography>
            <Typography variant="body2" color="text.secondary">{collab.branchName || 'N/A'}</Typography>
            <Typography variant="body2" color="text.secondary">{collab.buildTime || 'N/A'}</Typography>
          </Paper>
        </Box>
      )}

      {/* Microservice table */}
      <TableContainer component={Paper} variant="outlined" sx={{ mx: 1, mb: 1, maxHeight: 400, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: '30%' }}>Microservice</TableCell>
              <TableCell sx={{ fontWeight: 700, width: '10%' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, width: '18%' }}>Version</TableCell>
              <TableCell sx={{ fontWeight: 700, width: '22%' }}>Branch</TableCell>
              <TableCell sx={{ fontWeight: 700, width: '15%' }}>Build Time</TableCell>
              <TableCell sx={{ fontWeight: 700, width: '5%' }}>Raw</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {microservices.map((ms) => (
              <TableRow key={ms.alias} hover>
                <TableCell>
                  <Tooltip title={ms.alias} placement="top" arrow>
                    <Typography variant="body2" noWrap>{ms.displayName}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <HubStatusBadge status={ms.status} variant="dot" size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {ms.version || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {ms.branchName || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                    {ms.buildTime || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {ms.getCBVUrl ? (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View raw getCBV" arrow>
                        <Button
                          size="small"
                          sx={{ minWidth: 0, p: 0.25 }}
                          onClick={() => handleViewRaw(ms.displayName)}
                        >
                          View
                        </Button>
                      </Tooltip>
                      <Tooltip title="Open in new tab" arrow>
                        <Button
                          size="small"
                          sx={{ minWidth: 0, p: 0.25 }}
                          component="a"
                          href={ms.getCBVUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Button>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {microservices.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No microservice data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Raw getCBV modal for a specific MS — uses instanceName for the proxy call */}
      <RawCbvMsvModal
        open={rawModalOpen}
        onClose={() => { setRawModalOpen(false); setSelectedMsName(null); }}
        instanceName={instanceName}
        type="cbv"
        title={selectedMsName ? `Raw getCBV — ${selectedMsName}` : 'Raw getCBV'}
      />
    </Box>
  );
};

export default HubMicroservicePanel;
